import * as React from 'react';
import { Heading, Password } from '@built/design-system';

import {
  AuthErrorType,
  EmailVerificationData,
  ValidationErrorType
} from '../types';
import * as styles from './EmailVerification.module.scss';
import ActionButton from '../containers/ActionButton';
import * as ErrorNotifications from './ErrorNotifications';
import Notification from '../containers/Notification';
import l10n from '@built/i18n';

import { getValidationError } from '../utils';

export default function EmailVerification(props: EmailVerificationProps) {
  const { className } = props;
  const classes = [styles.verticalSpacing];
  if (className) {
    classes.push(className);
  }

  return (
    <div className={classes.join(' ')}>
      {renderHeading(props)}
      {renderError(props)}
      {renderView(props)}
    </div>
  );
}

function renderHeading(props: EmailVerificationProps) {
  return (
    <Heading size="xl" className="margin-b-md">
      Verify new email address
    </Heading>
  );
}

/**
 * Render errors that can happen during email verification
 */
function renderError(props: EmailVerificationProps) {
  // if we don't have any data or an error just return
  if (!props.emailVerificationData || !props.emailVerificationData.error) {
    return;
  }

  // render the appropriate error based on information we know and
  // using the helper components
  const error = props.emailVerificationData.error;
  switch (error.type) {
    case AuthErrorType.EMAIL_VERIFICATION_ERROR:
      return ErrorNotifications.genericError(error);
    case AuthErrorType.INVALID_CREDENTIALS_ERROR:
      return ErrorNotifications.invalidCredentialsError(error);
    default:
      return ErrorNotifications.unknownError();
  }
}

/**
 * Render the the rest of the email verification based on the
 * state
 */
function renderView(props: EmailVerificationProps) {
  const data = props.emailVerificationData;
  const error = data && data.error ? data.error : undefined;

  // if we couldn't verify the information from the link we fail immediately
  // and give the user an out back to the login view
  if (
    !data ||
    (error && error.type === AuthErrorType.EMAIL_VERIFICATION_ERROR)
  ) {
    return (
      <ActionButton
        loading={props.busy}
        label={l10n.t(
          'EmailVerification:ActionButton.backbutton',
          'Back to Log In'
        )}
        onClick={props.redirectToLogin}
      />
    );
  }

  if (props.complete) {
    return renderCompleteForm(props);
  }

  return renderPasswordForm(props);
}

function renderCompleteForm(props: EmailVerificationProps) {
  const email = props.emailVerificationData!.newEmail;

  return (
    <React.Fragment>
      <Notification
        type="success"
        title="Your email address has been verified!"
      >
        <p className={styles.lead}>
          You will now use the email{' '}
          <span className={styles.emailAddress}>{email}</span> each time you log
          into Built.
        </p>
      </Notification>
      <ActionButton
        loading={props.busy}
        label="Continue to Built"
        onClick={props.redirectToConsole}
      />
    </React.Fragment>
  );
}

function renderPasswordForm(props: EmailVerificationProps) {
  const email = props.emailVerificationData!.newEmail;
  const { showPasswordError } = props;

  const subtext = showPasswordError
    ? getValidationError(ValidationErrorType.VALIDATION_PASSWORD_ERROR)
    : '';
  const className = showPasswordError ? styles.passwordError : '';

  return (
    <form className={styles.form} onSubmit={props.verifyEmailAddress}>
      <p className="lead">
        Enter your password to complete the verification of your new email
        address <span className={styles.emailAddress}>{email}</span>.
      </p>
      <Password
        autoFocus={true}
        className={className}
        onChange={props.onPasswordInputChange}
        disabled={props.busy}
        name="password"
        label="Password"
        size="lg"
        subtext={subtext}
        value={props.password}
      />

      <ActionButton
        loading={props.busy}
        label="Verify new email address"
        type="submit"
      />
    </form>
  );
}

export interface EmailVerificationProps {
  busy?: boolean;

  /** Have we completed the email verification? */
  complete: boolean;

  /** An optional additional class name for the authorization component */
  className?: string;

  /** Email verification data needed */
  emailVerificationData?: EmailVerificationData;

  /** Password value */
  password: string;

  /** Whether to show the password error message */
  showPasswordError: boolean;

  /** Handle password input changes */
  onPasswordInputChange(event: React.SyntheticEvent<HTMLInputElement>): void;

  /** Kicks the user back to login view */
  redirectToLogin(event: React.SyntheticEvent<HTMLElement>): void;

  /** Redirect to built after successful email verification */
  redirectToConsole(event: React.SyntheticEvent<HTMLElement>): void;

  /** Starts the verification flow */
  verifyEmailAddress(event: React.SyntheticEvent<HTMLElement>): void;
}



// WEBPACK FOOTER //
// ./src/components/EmailVerification.tsx